import { CheckIcon, CloseIcon } from "@rothoblaas/design-system";
import classNames from "classnames";
import { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useI18n from "../../../../../hooks/useTranslations";
import { LC_IMG } from "../../constants";
import styles from "../../LockCalculator.module.css";
import type { LockCalculatorFormValues } from "../../model/form";
import { hasRealLoadValue } from "../../model/structuralVisibility";
import { NoticeFormField as FormField } from "../notices/NoticeFormField";

// ─── Force icons for combined toggles ───

const COMBINED_OPTIONS = [
  {
    name: "combinedVerification.includeVertical" as const,
    labelKey: "includeFv" as const,
    icon: `${LC_IMG}/vertical-shear.png`,
    loadKey: "vertical" as const,
  },
  {
    name: "combinedVerification.includeUplift" as const,
    labelKey: "includeFup" as const,
    icon: `${LC_IMG}/vertical-shear-uplift.png`,
    loadKey: "uplift" as const,
  },
  {
    name: "combinedVerification.includeAxial" as const,
    labelKey: "includeFax" as const,
    icon: `${LC_IMG}/axial.png`,
    loadKey: "axial" as const,
  },
  {
    name: "combinedVerification.includeLateral" as const,
    labelKey: "includeFlat" as const,
    icon: `${LC_IMG}/lateral-shear.png`,
    loadKey: "lateral" as const,
  },
];

// ─── Props ───

interface CombinedVerificationFormProps {
  readOnly?: boolean;
  onOpenHelp?: (text: string, image?: string) => void;
}

// ─── Component ───

export const CombinedVerificationForm: FC<CombinedVerificationFormProps> = ({
  readOnly = false,
  onOpenHelp,
}) => {
  const i18n = useI18n();
  const { control, setValue } = useFormContext<LockCalculatorFormValues>();
  const watched = useWatch({ control }) as LockCalculatorFormValues;
  const t = i18n.translation;
  const toggleOption = (name: (typeof COMBINED_OPTIONS)[number]["name"], isActive: boolean) => {
    const nextValue = !isActive;
    setValue(name, nextValue, { shouldDirty: true, shouldValidate: true });

    if (!nextValue) return;
    if (name === "combinedVerification.includeVertical") {
      setValue("combinedVerification.includeUplift", false, { shouldDirty: true, shouldValidate: true });
    } else if (name === "combinedVerification.includeUplift") {
      setValue("combinedVerification.includeVertical", false, { shouldDirty: true, shouldValidate: true });
    }
  };

  return (
    <div className={styles.compactFormStack}>
      <FormField
        name="combinedVerification"
        label={t.lockCalculatorModule?.combined?.title}
        isOptional
        showHelp={{
          tooltip: t.lockCalculatorModule?.helpTexts?.combined,
          onOpen: () => onOpenHelp?.(t.lockCalculatorModule?.helpTexts?.combined, `${LC_IMG}/combined-load-formula.png`),
        }}
      >
        <p style={{ fontSize: "0.82rem", color: "var(--grey-dark)", marginBottom: 0 }}>
          {t.lockCalculatorModule?.combined?.description}
        </p>

        <div className={`${styles.imageSelectGrid} ${styles.imageSelectGrid2}`}>
          {COMBINED_OPTIONS.map((option) => {
            const isActive = !!(
              option.name === "combinedVerification.includeVertical"
                ? watched.combinedVerification.includeVertical
                : option.name === "combinedVerification.includeUplift"
                  ? watched.combinedVerification.includeUplift
                  : option.name === "combinedVerification.includeAxial"
                    ? watched.combinedVerification.includeAxial
                    : watched.combinedVerification.includeLateral
            );
            const label = t.lockCalculatorModule?.combined?.[option.labelKey] ?? option.name;
            const hasLoad = hasRealLoadValue(watched.loads?.[option.loadKey]);
            return (
              <button
                key={option.name}
                type="button"
                className={classNames(styles.imageSelectOption, {
                  [styles.imageSelectOptionSelected]: isActive,
                })}
                aria-pressed={isActive}
                disabled={readOnly || !hasLoad}
                title={label}
                onClick={() => toggleOption(option.name, isActive)}
              >
                {isActive ? (
                  <CheckIcon aria-hidden className={classNames(styles.optionStatusIcon, styles.optionStatusOn)} />
                ) : (
                  <CloseIcon aria-hidden className={classNames(styles.optionStatusIcon, styles.optionStatusOff)} />
                )}
                <img className={styles.imageSelectImage} src={option.icon} alt={label} />
                <span className={styles.imageSelectLabel}>{label}</span>
              </button>
            );
          })}
        </div>
      </FormField>
    </div>
  );
};
